import React, { useState, useEffect } from 'react';
import { syncManager, connectionMonitor } from '../utils/syncManager';

const SyncStatus = () => {
  const [syncStatus, setSyncStatus] = useState(null);
  const [connectionHealth, setConnectionHealth] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const updateStatus = () => {
      try {
        setSyncStatus(syncManager.getSyncStatus());
        if (connectionMonitor && typeof connectionMonitor.getHealthStatus === 'function') {
          setConnectionHealth(connectionMonitor.getHealthStatus());
        } else {
          setConnectionHealth({ isHealthy: true, lastCheck: Date.now() });
        }
      } catch (error) {
        console.warn('Sync status update failed:', error);
        setSyncStatus({ isOnline: true, queuedOperations: 0, activeSubscriptions: 0, retryingSubscriptions: 0 });
        setConnectionHealth({ isHealthy: true, lastCheck: Date.now() });
      }
    };

    // Initial update
    updateStatus();

    // Update every 15 seconds to reduce polling
    const interval = setInterval(() => {
      // Only update if component is visible
      if (!document.hidden) {
        updateStatus();
      }
    }, 15000);

    // Listen for sync events with throttling
    let lastErrorLog = 0;
    const handleSyncError = (event) => {
      const now = Date.now();
      // Throttle error logging to prevent spam
      if (now - lastErrorLog > 10000) {
        console.warn('Sync error in status component:', event.detail);
        lastErrorLog = now;
      }
      updateStatus();
    };

    let lastConnectionLog = 0;
    const handleConnectionChange = (event) => {
      const now = Date.now();
      // Throttle connection change logging
      if (now - lastConnectionLog > 5000) {
        console.log('Connection change in status component:', event.detail);
        lastConnectionLog = now;
      }
      updateStatus();
    };

    window.addEventListener('sync-error', handleSyncError);
    window.addEventListener('connection-health-change', handleConnectionChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('sync-error', handleSyncError);
      window.removeEventListener('connection-health-change', handleConnectionChange);
    };
  }, []);

  // Only show in development or when explicitly enabled
  const shouldShow = 
    window.location.hostname === 'localhost' ||
    localStorage.getItem('show-sync-status') === 'true';

  if (!shouldShow || !syncStatus || !connectionHealth) return null;

  const getStatusColor = () => {
    if (!syncStatus.isOnline) return 'bg-red-500';
    if (!connectionHealth.isHealthy) return 'bg-yellow-500';
    if (syncStatus.queuedOperations > 0) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const getStatusText = () => {
    if (!syncStatus.isOnline) return 'Offline';
    if (!connectionHealth.isHealthy) return 'Connection Issues';
    if (syncStatus.queuedOperations > 0) return `Syncing (${syncStatus.queuedOperations})`;
    return 'Online';
  };

  return (
    <div className="fixed top-4 right-4 z-50">
      <div 
        className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg shadow-lg px-3 py-2 cursor-pointer"
        onClick={() => setShowDetails(!showDetails)}
      >
        <div className={`w-3 h-3 rounded-full ${getStatusColor()}`}></div>
        <span className="text-sm font-medium text-gray-700">
          {getStatusText()}
        </span>
      </div>

      {showDetails && (
        <div className="absolute top-full right-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg p-4 min-w-64">
          <h3 className="font-semibold text-gray-800 mb-3">Sync Status</h3>
          
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Network:</span>
              <span className={syncStatus.isOnline ? 'text-green-600' : 'text-red-600'}>
                {syncStatus.isOnline ? 'Online' : 'Offline'}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span>Connection:</span>
              <span className={connectionHealth.isHealthy ? 'text-green-600' : 'text-yellow-600'}>
                {connectionHealth.isHealthy ? 'Healthy' : 'Issues'}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span>Queued Operations:</span>
              <span className="font-mono">{syncStatus.queuedOperations}</span>
            </div>
            
            <div className="flex justify-between">
              <span>Active Subscriptions:</span>
              <span className="font-mono">{syncStatus.activeSubscriptions}</span>
            </div>
            
            <div className="flex justify-between">
              <span>Retrying:</span>
              <span className="font-mono">{syncStatus.retryingSubscriptions}</span>
            </div>
            
            <div className="flex justify-between">
              <span>Last Check:</span>
              <span className="text-xs text-gray-500">
                {Math.round((Date.now() - connectionHealth.lastCheck) / 1000)}s ago
              </span>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-gray-200">
            <button
              onClick={() => {
                try {
                  if (syncManager && typeof syncManager.processSyncQueue === 'function') {
                    syncManager.processSyncQueue();
                  }
                  if (connectionMonitor && typeof connectionMonitor.checkConnection === 'function') {
                    connectionMonitor.checkConnection();
                  }
                } catch (error) {
                  console.warn('Force sync failed:', error);
                }
              }}
              className="w-full bg-blue-600 text-white text-xs px-3 py-2 rounded hover:bg-blue-700"
            >
              Force Sync
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SyncStatus;